/**
 * IIT / NEET Foundation Student Dashboard — dummy data (single source).
 *
 * Plain JSON: no logic, no imports. Every section of the dashboard reads from
 * this object through props, so replacing it with an API response requires no
 * component changes.
 *
 * Sections: header · quickActions · kpis · weekly · mastery · examAnalysis
 *           strengths · planner · leaderboard · attendance
 */

export const section1DummyData = {
  /* ----------------------------------------------------------- header --- */
  student: {
    id: "RZ-NEETF-2026-01287",
    name: "Rahul Verma",
    firstName: "Rahul",
    // avatar: "https://randomuser.me/api/portraits/men/45.jpg",
    program: "NEET Foundation",
    tagline: "Ready to continue your NEET Foundation journey?"
  },

  highlights: [
    { id: "class", label: "Class", value: "9th", accent: true, emoji: "", hint: "" },
    { id: "program", label: "Program", value: "NEET Foundation", accent: false, emoji: "", hint: "" },
    { id: "rank", label: "Current Rank", value: "#1287", accent: false, emoji: "🏆", hint: "in your batch" }
  ],

  grade: {
    label: "Overall Grade",
    grade: "A+",
    percentage: 88.6,
    gpa: 9.2,
    hint: "Term II average",
    delta: 3.4
  },

  /* ---------------------------------------------------- quick actions --- */
  quickActions: {
    title: "Quick Actions",
    subtitle: "Pick up exactly where you left off",
    actionLabel: "Edit Shortcuts",
    cards: [
      {
        id: "continue",
        theme: "blue",
        icon: "fas fa-book-open",
        title: "Continue Learning",
        description: "Resume the chapter you left halfway",
        metricValue: "8",
        metricLabel: "chapters pending",
        badge: "65% complete",
        badgeTone: "soft",
        progress: 65,
        progressLabel: "13 of 20 chapters done",
        cta: "Resume Chapter"
      },
      {
        id: "mock",
        theme: "purple",
        icon: "fas fa-file-alt",
        title: "Mock Test",
        description: "Attempt today's full-length paper",
        metricValue: "3",
        metricLabel: "mock tests available",
        badge: "New test live",
        badgeTone: "new",
        progress: 40,
        progressLabel: "2 of 5 attempted this month",
        cta: "Start Mock Test"
      },
      {
        id: "errors",
        theme: "orange",
        icon: "fas fa-exclamation-triangle",
        title: "Error Analysis",
        description: "Fix the mistakes that cost you marks",
        metricValue: "24",
        metricLabel: "mistakes to review",
        badge: "Needs attention",
        badgeTone: "soft",
        progress: 35,
        progressLabel: "9 of 24 corrected",
        cta: "Review Mistakes"
      }
    ]
  },

  /* ------------------------------------------------------- KPI strip --- */
  kpis: {
    title: "Performance Overview",
    subtitle: "Your numbers across the whole programme",
    cards: [
      { id: "score", label: "Overall Score", value: 86.4, decimals: 1, suffix: "%", caption: "vs 82.1% last month", icon: "fas fa-chart-line", tone: "blue", delta: 4.3, progress: 86.4 },
      { id: "accuracy", label: "Accuracy", value: 81.7, decimals: 1, suffix: "%", caption: "1,284 of 1,572 correct", icon: "fas fa-bullseye", tone: "green", delta: 2.6, progress: 81.7 },
      { id: "tests", label: "Tests Taken", value: 18, caption: "2 scheduled this week", icon: "fas fa-file-alt", tone: "purple", delta: 3, progress: 60 },
      { id: "rank", label: "Batch Rank", value: 1287, prefix: "#", caption: "Out of 4,200 students", icon: "fas fa-trophy", tone: "orange", delta: 96, progress: 70 },
      { id: "syllabus", label: "Syllabus", value: 68, suffix: "%", caption: "34 of 50 chapters", icon: "fas fa-book-open", tone: "teal", delta: 6, progress: 68 },
      { id: "streak", label: "Study Streak", value: 14, suffix: "d", caption: "Personal best 29 days", icon: "fas fa-fire", tone: "red", delta: 4, progress: 48 }
    ]
  },

  /* ------------------------------------------- weekly performance card --- */
  weekly: {
    title: "Weekly Performance",
    subtitle: "Mon - Sun • this week",
    badge: "+12% vs last week",
    kpis: [
      { id: "hours", label: "Study Hours", value: 26.5, decimals: 1, suffix: "h", icon: "fas fa-clock", tone: "blue", delta: 3.5 },
      { id: "chapters", label: "Chapters", value: 6, icon: "fas fa-book-open", tone: "purple", delta: 2 },
      { id: "topics", label: "Topics", value: 18, icon: "fas fa-layer-group", tone: "teal", delta: 4 },
      { id: "questions", label: "Questions", value: 412, icon: "fas fa-pen", tone: "orange", delta: 68 },
      { id: "accuracy", label: "Accuracy", value: 84.2, decimals: 1, suffix: "%", icon: "fas fa-bullseye", tone: "green", delta: 2.6 }
    ],
    trend: {
      days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      questions: [52, 68, 44, 74, 61, 78, 35],
      accuracy: [79, 83, 76, 86, 82, 88, 81]
    }
  },

  /* ------------------------------------------------ subject mastery --- */
  mastery: {
    title: "Subject Mastery",
    subtitle: "Questions, pace and accuracy per subject",
    badge: "4 subjects",
    subjects: [
      { id: "maths", subject: "Mathematics", icon: "fas fa-square-root-alt", questionsSolved: 465, avgTimePerQuestion: 96, accuracy: 72.8, mastery: 68 },
      { id: "physics", subject: "Physics", icon: "fas fa-atom", questionsSolved: 420, avgTimePerQuestion: 72, accuracy: 84.2, mastery: 81 },
      { id: "chemistry", subject: "Chemistry", icon: "fas fa-flask", questionsSolved: 398, avgTimePerQuestion: 64, accuracy: 86.7, mastery: 87 },
      { id: "biology", subject: "Biology", icon: "fas fa-dna", questionsSolved: 310, avgTimePerQuestion: 58, accuracy: 89.5, mastery: 92 }
    ]
  },

  /* -------------------------------------------------- exam analysis --- */
  examAnalysis: {
    title: "Exam Analysis",
    subtitle: "Your last five attempts",
    badge: "Best: 92%",
    columns: ["Exam", "Date", "Score", "Accuracy", "Rank", "Result", ""],
    exams: [
      { id: "e1", name: "Foundation Mock Test - 08", type: "Mock", date: "02 Aug", score: 268, total: 300, percentage: 89.3, accuracy: 91.2, rank: 84, outOf: 4200 },
      { id: "e2", name: "Weekly Test - Biology", type: "Weekly", date: "27 Jul", score: 46, total: 50, percentage: 92.0, accuracy: 93.5, rank: 61, outOf: 4200 },
      { id: "e3", name: "Chapter Test - Motion", type: "Chapter", date: "22 Jul", score: 34, total: 50, percentage: 68.0, accuracy: 70.4, rank: 1642, outOf: 4200 },
      { id: "e4", name: "Foundation Mock Test - 07", type: "Mock", date: "16 Jul", score: 246, total: 300, percentage: 82.0, accuracy: 84.1, rank: 218, outOf: 4200 },
      { id: "e5", name: "Weekly Test - Chemistry", type: "Weekly", date: "10 Jul", score: 41, total: 50, percentage: 82.0, accuracy: 83.6, rank: 264, outOf: 4200 }
    ]
  },


  /* ================================================================
     Exam Analysis — student view (Class 6-10 friendly).
     `default` is used for any exam without its own detailed record.
     ================================================================ */
  examAnalysisDetail: {
    e1: {
      motivation: {
        emoji: "🎉",
        title: "Excellent work! Keep it up!",
        message: "You scored 89.3% and jumped 96 ranks. Two more practice sets in Mathematics and you will cross 90%.",
        tone: "excellent"
      },
      overall: {
        totalMarks: 268,
        maxMarks: 300,
        percentage: 89.3,
        rank: 84,
        outOf: 4200,
        grade: "A+",
        totalQuestions: 75,
        attempted: 71,
        correct: 66,
        wrong: 5,
        unattempted: 4,
        accuracy: 92.9,
        timeTaken: "168 min",
        totalTime: "180 min"
      },
      subjects: [
        { id: "s1", subject: "Mathematics", icon: "fas fa-square-root-alt", tone: "blue", marks: 64, total: 75, correct: 16, wrong: 2, unattempted: 1, accuracy: 88.9 },
        { id: "s2", subject: "Physics", icon: "fas fa-atom", tone: "purple", marks: 70, total: 75, correct: 17, wrong: 1, unattempted: 1, accuracy: 94.4 },
        { id: "s3", subject: "Chemistry", icon: "fas fa-flask", tone: "orange", marks: 66, total: 75, correct: 16, wrong: 2, unattempted: 1, accuracy: 88.9 },
        { id: "s4", subject: "Biology", icon: "fas fa-dna", tone: "green", marks: 68, total: 75, correct: 17, wrong: 0, unattempted: 1, accuracy: 100 }
      ],
      chapters: [
        { id: "c1", chapter: "Fractions & Decimals", subject: "Mathematics", questions: 8, correct: 7, wrong: 1, accuracy: 87.5, status: "Completed" },
        { id: "c2", chapter: "Algebra Basics", subject: "Mathematics", questions: 9, correct: 6, wrong: 3, accuracy: 66.7, status: "Revise" },
        { id: "c3", chapter: "Motion", subject: "Physics", questions: 9, correct: 9, wrong: 0, accuracy: 100, status: "Completed" },
        { id: "c4", chapter: "Force & Laws", subject: "Physics", questions: 9, correct: 8, wrong: 1, accuracy: 88.9, status: "Completed" },
        { id: "c5", chapter: "Atoms & Molecules", subject: "Chemistry", questions: 9, correct: 8, wrong: 1, accuracy: 88.9, status: "Completed" },
        { id: "c6", chapter: "Chemical Reactions", subject: "Chemistry", questions: 9, correct: 6, wrong: 3, accuracy: 66.7, status: "Revise" },
        { id: "c7", chapter: "Cell Structure", subject: "Biology", questions: 10, correct: 10, wrong: 0, accuracy: 100, status: "Completed" },
        { id: "c8", chapter: "Tissues", subject: "Biology", questions: 8, correct: 7, wrong: 0, accuracy: 87.5, status: "In progress" }
      ],
      topics: [
        { id: "t1", topic: "Adding Fractions", subject: "Mathematics", solved: 5, accuracy: 100, status: "Strong" },
        { id: "t2", topic: "Simple Equations", subject: "Mathematics", solved: 6, accuracy: 66.7, status: "Practice More" },
        { id: "t3", topic: "Speed & Velocity", subject: "Physics", solved: 6, accuracy: 100, status: "Strong" },
        { id: "t4", topic: "Newton's Laws", subject: "Physics", solved: 5, accuracy: 80, status: "Good" },
        { id: "t5", topic: "Mole Concept", subject: "Chemistry", solved: 5, accuracy: 60, status: "Practice More" },
        { id: "t6", topic: "Periodic Table", subject: "Chemistry", solved: 6, accuracy: 100, status: "Strong" },
        { id: "t7", topic: "Plant Cell", subject: "Biology", solved: 6, accuracy: 100, status: "Strong" },
        { id: "t8", topic: "Animal Tissues", subject: "Biology", solved: 5, accuracy: 80, status: "Good" }
      ],
      questions: [
        {
          id: "q1", no: 1, subject: "Mathematics", chapter: "Fractions & Decimals", topic: "Adding Fractions",
          status: "correct", marks: 4, timeTaken: 55,
          question: "What is 1/2 + 1/4 ?",
          options: [
            { key: "A", text: "2/6" },
            { key: "B", text: "3/4" },
            { key: "C", text: "1/6" },
            { key: "D", text: "2/4" }
          ],
          correctAnswer: "B",
          studentAnswer: "B",
          explanation: "Make the denominators the same: 1/2 becomes 2/4. Then 2/4 + 1/4 = 3/4."
        },
        {
          id: "q2", no: 2, subject: "Mathematics", chapter: "Algebra Basics", topic: "Simple Equations",
          status: "wrong", marks: -1, timeTaken: 96,
          question: "If x + 7 = 12, what is the value of x ?",
          options: [
            { key: "A", text: "19" },
            { key: "B", text: "7" },
            { key: "C", text: "5" },
            { key: "D", text: "12" }
          ],
          correctAnswer: "C",
          studentAnswer: "A",
          explanation: "Take 7 away from both sides: x = 12 - 7 = 5. You added instead of subtracting."
        },
        {
          id: "q3", no: 3, subject: "Physics", chapter: "Motion", topic: "Speed & Velocity",
          status: "correct", marks: 4, timeTaken: 62,
          question: "A car travels 120 km in 2 hours. What is its average speed?",
          options: [
            { key: "A", text: "40 km/h" },
            { key: "B", text: "60 km/h" },
            { key: "C", text: "120 km/h" },
            { key: "D", text: "240 km/h" }
          ],
          correctAnswer: "B",
          studentAnswer: "B",
          explanation: "Speed = distance ÷ time = 120 ÷ 2 = 60 km/h."
        },
        {
          id: "q4", no: 4, subject: "Physics", chapter: "Force & Laws", topic: "Newton's Laws",
          status: "wrong", marks: -1, timeTaken: 88,
          question: "Which law says 'every action has an equal and opposite reaction'?",
          options: [
            { key: "A", text: "Newton's First Law" },
            { key: "B", text: "Newton's Second Law" },
            { key: "C", text: "Newton's Third Law" },
            { key: "D", text: "Law of Gravitation" }
          ],
          correctAnswer: "C",
          studentAnswer: "B",
          explanation: "The Third Law is about action and reaction pairs. The Second Law is F = m × a."
        },
        {
          id: "q5", no: 5, subject: "Chemistry", chapter: "Chemical Reactions", topic: "Mole Concept",
          status: "unattempted", marks: 0, timeTaken: 0,
          question: "How many grams are there in 1 mole of water (H₂O)?",
          options: [
            { key: "A", text: "9 g" },
            { key: "B", text: "16 g" },
            { key: "C", text: "18 g" },
            { key: "D", text: "36 g" }
          ],
          correctAnswer: "C",
          studentAnswer: null,
          explanation: "Water = 2 hydrogen (1 g each) + 1 oxygen (16 g) = 18 g. You skipped this one — try it again!"
        },
        {
          id: "q6", no: 6, subject: "Biology", chapter: "Cell Structure", topic: "Plant Cell",
          status: "correct", marks: 4, timeTaken: 44,
          question: "Which part of the plant cell makes food using sunlight?",
          options: [
            { key: "A", text: "Nucleus" },
            { key: "B", text: "Chloroplast" },
            { key: "C", text: "Vacuole" },
            { key: "D", text: "Cell wall" }
          ],
          correctAnswer: "B",
          studentAnswer: "B",
          explanation: "Chloroplasts contain green chlorophyll, which traps sunlight to make food."
        }
      ],
      learning: {
        title: "Learning Progress",
        items: [
          { id: "lp1", emoji: "⏰", label: "Study Hours", value: 26.5, decimals: 1, suffix: "h", tone: "blue" },
          { id: "lp2", emoji: "✏️", label: "Questions Solved", value: 412, tone: "purple" },
          { id: "lp3", emoji: "📗", label: "Chapters Completed", value: 6, tone: "green" },
          { id: "lp4", emoji: "🧩", label: "Topics Covered", value: 18, tone: "orange" }
        ],
        overallProgress: 74,
        progressLabel: "You have finished 74% of this term's syllabus"
      },
      strengths: [
        { id: "st1", emoji: "🏆", label: "Best Subject", value: "Biology", detail: "100% accuracy — 17 of 17 correct" },
        { id: "st2", emoji: "📘", label: "Best Chapter", value: "Cell Structure", detail: "All 10 questions correct" },
        { id: "st3", emoji: "🎯", label: "Highest Accuracy Topic", value: "Speed & Velocity", detail: "100% in 6 questions" },
        { id: "st4", emoji: "⚡", label: "Fastest Subject", value: "Biology", detail: "58 seconds per question" }
      ],
      improve: [
        { id: "im1", emoji: "📐", label: "Practice this subject", value: "Mathematics", detail: "88.9% — the lowest of your four subjects" },
        { id: "im2", emoji: "🔁", label: "Chapters to revise", value: "Algebra Basics, Chemical Reactions", detail: "Both at 66.7% accuracy" },
        { id: "im3", emoji: "🧪", label: "Low accuracy topics", value: "Mole Concept, Simple Equations", detail: "60% and 66.7%" },
        { id: "im4", emoji: "↩️", label: "Questions to reattempt", value: "5 wrong + 4 skipped", detail: "9 questions worth 36 marks" }
      ],
      recommendations: [
        { id: "rc1", emoji: "🍕", tone: "blue", title: "Revise Fractions", subject: "Mathematics", topic: "Fractions & Decimals", questions: 15, time: "20 min", cta: "Start Practice" },
        { id: "rc2", emoji: "📐", tone: "purple", title: "Practice Algebra", subject: "Mathematics", topic: "Simple Equations", questions: 20, time: "30 min", cta: "Start Practice" },
        { id: "rc3", emoji: "🚀", tone: "orange", title: "Solve 20 Physics Questions", subject: "Physics", topic: "Force & Laws", questions: 20, time: "25 min", cta: "Start Practice" },
        { id: "rc4", emoji: "🧪", tone: "green", title: "Complete Chemistry Worksheet", subject: "Chemistry", topic: "Chemical Reactions", questions: 18, time: "25 min", cta: "Start Practice" },
        { id: "rc5", emoji: "↩️", tone: "pink", title: "Retry Incorrect Questions", subject: "All Subjects", topic: "From this test", questions: 9, time: "15 min", cta: "Start Practice" },
        { id: "rc6", emoji: "📝", tone: "teal", title: "Finish Pending Chapter Test", subject: "Biology", topic: "Tissues", questions: 25, time: "35 min", cta: "Start Practice" }
      ],
      achievements: [
        { id: "ac1", emoji: "⭐", title: "Accuracy Star", detail: "Crossed 90% accuracy", unlocked: true },
        { id: "ac2", emoji: "🔥", title: "Study Streak", detail: "14 days in a row", unlocked: true },
        { id: "ac3", emoji: "🏅", title: "Chapter Master", detail: "Cell Structure — all correct", unlocked: true },
        { id: "ac4", emoji: "📚", title: "Practice Champion", detail: "400+ questions this month", unlocked: true },
        { id: "ac5", emoji: "🎯", title: "Consistency Badge", detail: "6 more days to unlock", unlocked: false, progress: 70 }
      ]
    },

    default: {
      motivation: {
        emoji: "💪",
        title: "You're improving every week!",
        message: "Good effort in this test. Revise the chapters below and your next score will be even better.",
        tone: "good"
      },
      overall: {
        totalMarks: 34,
        maxMarks: 50,
        percentage: 68,
        rank: 1642,
        outOf: 4200,
        grade: "B",
        totalQuestions: 25,
        attempted: 22,
        correct: 17,
        wrong: 5,
        unattempted: 3,
        accuracy: 77.3,
        timeTaken: "44 min",
        totalTime: "50 min"
      },
      subjects: [
        { id: "ds1", subject: "Physics", icon: "fas fa-atom", tone: "purple", marks: 34, total: 50, correct: 17, wrong: 5, unattempted: 3, accuracy: 77.3 }
      ],
      chapters: [
        { id: "dc1", chapter: "Motion", subject: "Physics", questions: 13, correct: 10, wrong: 3, accuracy: 76.9, status: "Revise" },
        { id: "dc2", chapter: "Speed & Distance", subject: "Physics", questions: 12, correct: 7, wrong: 2, accuracy: 58.3, status: "Revise" }
      ],
      topics: [
        { id: "dt1", topic: "Distance vs Displacement", subject: "Physics", solved: 6, accuracy: 83.3, status: "Good" },
        { id: "dt2", topic: "Average Speed", subject: "Physics", solved: 7, accuracy: 57.1, status: "Practice More" },
        { id: "dt3", topic: "Graphs of Motion", subject: "Physics", solved: 5, accuracy: 60, status: "Practice More" },
        { id: "dt4", topic: "Uniform Motion", subject: "Physics", solved: 4, accuracy: 100, status: "Strong" }
      ],
      questions: [
        {
          id: "dq1", no: 1, subject: "Physics", chapter: "Motion", topic: "Uniform Motion",
          status: "correct", marks: 4, timeTaken: 48,
          question: "A body covers equal distances in equal time. This motion is called:",
          options: [
            { key: "A", text: "Uniform motion" },
            { key: "B", text: "Circular motion" },
            { key: "C", text: "Random motion" },
            { key: "D", text: "Vibratory motion" }
          ],
          correctAnswer: "A",
          studentAnswer: "A",
          explanation: "Equal distance in equal time means the speed never changes — that is uniform motion."
        },
        {
          id: "dq2", no: 2, subject: "Physics", chapter: "Speed & Distance", topic: "Average Speed",
          status: "wrong", marks: -1, timeTaken: 104,
          question: "A cyclist rides 30 km in 2 hours, then 30 km in 1 hour. What is the average speed?",
          options: [
            { key: "A", text: "15 km/h" },
            { key: "B", text: "20 km/h" },
            { key: "C", text: "30 km/h" },
            { key: "D", text: "45 km/h" }
          ],
          correctAnswer: "B",
          studentAnswer: "C",
          explanation: "Average speed = total distance ÷ total time = 60 ÷ 3 = 20 km/h. Do not average the two speeds."
        },
        {
          id: "dq3", no: 3, subject: "Physics", chapter: "Speed & Distance", topic: "Graphs of Motion",
          status: "unattempted", marks: 0, timeTaken: 0,
          question: "On a distance-time graph, a straight flat line means the object is:",
          options: [
            { key: "A", text: "Speeding up" },
            { key: "B", text: "Slowing down" },
            { key: "C", text: "At rest" },
            { key: "D", text: "Moving fast" }
          ],
          correctAnswer: "C",
          studentAnswer: null,
          explanation: "A flat line means the distance is not changing, so the object is standing still."
        },
        {
          id: "dq4", no: 4, subject: "Physics", chapter: "Motion", topic: "Distance vs Displacement",
          status: "correct", marks: 4, timeTaken: 66,
          question: "You walk 4 m east, then 4 m west. What is your displacement?",
          options: [
            { key: "A", text: "8 m" },
            { key: "B", text: "4 m" },
            { key: "C", text: "0 m" },
            { key: "D", text: "16 m" }
          ],
          correctAnswer: "C",
          studentAnswer: "C",
          explanation: "You finished where you started, so displacement is zero. Distance travelled is 8 m."
        }
      ],
      learning: {
        title: "Learning Progress",
        items: [
          { id: "dlp1", emoji: "⏰", label: "Study Hours", value: 18.5, decimals: 1, suffix: "h", tone: "blue" },
          { id: "dlp2", emoji: "✏️", label: "Questions Solved", value: 186, tone: "purple" },
          { id: "dlp3", emoji: "📗", label: "Chapters Completed", value: 3, tone: "green" },
          { id: "dlp4", emoji: "🧩", label: "Topics Covered", value: 9, tone: "orange" }
        ],
        overallProgress: 52,
        progressLabel: "You have finished 52% of this chapter's practice"
      },
      strengths: [
        { id: "dst1", emoji: "🏆", label: "Best Subject", value: "Physics", detail: "77.3% accuracy" },
        { id: "dst2", emoji: "📘", label: "Best Chapter", value: "Motion", detail: "10 of 13 correct" },
        { id: "dst3", emoji: "🎯", label: "Highest Accuracy Topic", value: "Uniform Motion", detail: "100% in 4 questions" },
        { id: "dst4", emoji: "⚡", label: "Fastest Subject", value: "Physics", detail: "72 seconds per question" }
      ],
      improve: [
        { id: "dim1", emoji: "📐", label: "Practice this subject", value: "Physics", detail: "77.3% — aim for 85%" },
        { id: "dim2", emoji: "🔁", label: "Chapters to revise", value: "Speed & Distance", detail: "58.3% accuracy" },
        { id: "dim3", emoji: "🧪", label: "Low accuracy topics", value: "Average Speed, Graphs of Motion", detail: "57.1% and 60%" },
        { id: "dim4", emoji: "↩️", label: "Questions to reattempt", value: "5 wrong + 3 skipped", detail: "8 questions worth 16 marks" }
      ],
      recommendations: [
        { id: "drc1", emoji: "🏃", tone: "blue", title: "Practice Average Speed", subject: "Physics", topic: "Average Speed", questions: 15, time: "20 min", cta: "Start Practice" },
        { id: "drc2", emoji: "📈", tone: "purple", title: "Revise Motion Graphs", subject: "Physics", topic: "Graphs of Motion", questions: 12, time: "18 min", cta: "Start Practice" },
        { id: "drc3", emoji: "↩️", tone: "pink", title: "Retry Incorrect Questions", subject: "Physics", topic: "From this test", questions: 8, time: "12 min", cta: "Start Practice" },
        { id: "drc4", emoji: "📝", tone: "teal", title: "Finish Pending Chapter Test", subject: "Physics", topic: "Speed & Distance", questions: 20, time: "30 min", cta: "Start Practice" }
      ],
      achievements: [
        { id: "dac1", emoji: "⭐", title: "Accuracy Star", detail: "Reach 90% accuracy", unlocked: false, progress: 77 },
        { id: "dac2", emoji: "🔥", title: "Study Streak", detail: "14 days in a row", unlocked: true },
        { id: "dac3", emoji: "🏅", title: "Chapter Master", detail: "Finish Speed & Distance", unlocked: false, progress: 58 },
        { id: "dac4", emoji: "📚", title: "Practice Champion", detail: "186 questions this month", unlocked: true },
        { id: "dac5", emoji: "🎯", title: "Consistency Badge", detail: "Practise 5 days this week", unlocked: false, progress: 60 }
      ]
    }
  },

  /* ------------------------------------------------- study planner --- */
  planner: {
    title: "Study Planner",
    subtitle: "This week's schedule",
    badge: "2 of 6 done",
    tasks: [
      { id: "p1", day: "Mon", date: "10 Aug", subject: "Chemistry", task: "Organic basics — concept revision", duration: "90 min", priority: "High", done: true },
      { id: "p2", day: "Tue", date: "11 Aug", subject: "Mathematics", task: "Algebra — 40 timed questions", duration: "75 min", priority: "High", done: true },
      { id: "p3", day: "Wed", date: "12 Aug", subject: "Physics", task: "Laws of Motion — previous papers", duration: "60 min", priority: "Medium", done: false },
      { id: "p4", day: "Thu", date: "13 Aug", subject: "Biology", task: "Cell structure — diagram practice", duration: "45 min", priority: "Low", done: false },
      { id: "p5", day: "Fri", date: "14 Aug", subject: "Mathematics", task: "Mentor doubt session", duration: "45 min", priority: "High", done: false }
    ]
  },

  /* --------------------------------------------------- leaderboard --- */
  leaderboard: {
    title: "Leaderboard",
    subtitle: "Top performers in your batch",
    badge: "Weekly",
    students: [
      { id: "l1", rank: 1, name: "Ananya Iyer", score: 94.2, avatar: "https://randomuser.me/api/portraits/women/68.jpg", delta: 0, isYou: false },
      { id: "l2", rank: 2, name: "Kabir Menon", score: 91.8, avatar: "https://randomuser.me/api/portraits/men/22.jpg", delta: 2, isYou: false },
      { id: "l3", rank: 3, name: "Rahul Verma", score: 88.6, avatar: "https://randomuser.me/api/portraits/men/45.jpg", delta: 4, isYou: true },
      { id: "l4", rank: 4, name: "Sana Qureshi", score: 87.1, avatar: "https://randomuser.me/api/portraits/women/31.jpg", delta: -1, isYou: false },
      { id: "l5", rank: 5, name: "Dev Malhotra", score: 85.4, avatar: "https://randomuser.me/api/portraits/men/12.jpg", delta: 1, isYou: false }
    ]
  },

  /* ----------------------------------------------------- attendance --- */
  attendance: {
    title: "Attendance",
    subtitle: "Academic year 2025 - 2026",
    badge: "92.4%",
    percentage: 92.4,
    present: 168,
    absent: 14,
    total: 182,
    lateArrivals: 6,
    months: ["Mar", "Apr", "May", "Jun", "Jul", "Aug"],
    monthly: [88, 92, 89, 96, 92, 96],
    bySubject: [
      { id: "a1", subject: "Mathematics", attended: 50, total: 52 },
      { id: "a2", subject: "Physics", attended: 44, total: 48 },
      { id: "a3", subject: "Chemistry", attended: 42, total: 46 },
      { id: "a4", subject: "Biology", attended: 32, total: 36 }
    ]
  }
};

export default section1DummyData;
